import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/api/client";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("has_session", "1");
      // Nudge staff who never rotated their imported password. Public share
      // links (/exam, /onboarding, /comics) already bypass this check via
      // the router-level guard so we don't hijack a vendor mid-quiz.
      if (data?.must_change_password) {
        const p = window.location.pathname || "";
        const bypass =
          p.startsWith("/change-password") ||
          p.startsWith("/exam/") ||
          p.startsWith("/onboarding/") ||
          p.startsWith("/comics") ||
          p.startsWith("/biblioteca") ||
          p.startsWith("/invite/") ||
          p.startsWith("/manager/");
        if (!bypass) {
          window.location.replace("/change-password");
        }
      }
    } catch {
      setUser(null);
      localStorage.removeItem("has_session");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from Emergent OAuth (hash) callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Post-OAuth landing (Google or Microsoft) — cookie is set server-side but
    // localStorage doesn't know yet. Force a /me check when we detect the marker.
    const url = new URL(window.location.href);
    const justLoggedIn = url.searchParams.get("login");
    if (justLoggedIn) {
      url.searchParams.delete("login");
      window.history.replaceState({}, "", url.toString());
      checkAuth();
      return;
    }
    // Skip /me on public routes when no prior session marker exists (keeps console clean)
    if (!localStorage.getItem("has_session")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("has_session");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
